import ReactApexChart from 'react-apexcharts';

import { ApexOptions } from 'apexcharts';
import { useTheme } from 'styled-components';

import round from 'utils/round';

interface IBasicDonutChart {
	value_name?: string[];
	value: number[];
	color?: string[];
	width?: string;
	height?: string;
	unit?: string;
	max_label?: boolean;
	legend_show?: boolean;
}

/**
 * BasicDonutChart 컴포넌트
 *
 * @param {string[]} value_name - 도넛 차트의 각 섹션에 대한 이름 배열
 * @param {number[]} value - 도넛 차트의 각 섹션에 대한 값 배열
 * @param {string[]} [color] - 도넛 차트의 각 섹션에 대한 색상 배열 (선택 사항)
 * @param {string | number} [width] - 도넛 차트의 너비 (선택 사항, 기본값: '100%')
 * @param {string | number} [height] - 도넛 차트의 높이 (선택 사항, 기본값: '100%')
 * @param {string} [unit] - 값에 대한 단위 (선택 사항)
 * @param {boolean} [max_label] - 최고 수치 레이블 표시 여부 (선택 사항)
 */
const BasicDonutChart = ({
	value_name,
	value,
	color,
	width,
	height,
	unit,
	max_label,
	legend_show,
}: IBasicDonutChart) => {
	const theme = useTheme();

	const max_value = Math.max(...value);
	const max_index = value.indexOf(max_value);

	const colors = color
		? [
				...color,
				theme.colors.uplus.bg30,
				theme.colors.uplus.bg20,
				theme.colors.uplus.bg15,
				...Array.from(
					{ length: value.length },
					(_, i) => theme.colors.uplus.bg10_lighten,
				),
		  ]
		: [
				'#FF5452',
				'#FF9391',
				'#FFD2D1',
				theme.colors.uplus.bg30,
				theme.colors.uplus.bg20,
				theme.colors.uplus.bg15,
				...Array.from(
					{ length: value.length },
					(_, i) => theme.colors.uplus.bg10_lighten,
				),
		  ];

	const sortedIndexes = value.map((v, i) => i).sort((a, b) => value[b] - value[a]);
	const sorted_color = Array.from(
		{ length: value.length },
		(_, i) => theme.colors.gray.gray80_text,
	);
	sortedIndexes.forEach((v, i) => {
		sorted_color[v] = colors[i];
	});

	const options: ApexOptions = {
		chart: {
			type: 'donut',
			width: width ?? '100%',
			height: height ?? '100%',
		},
		legend: {
			show: legend_show ?? true,
			offsetY: 0,
			position: 'right',
			fontSize: '12px',
			fontFamily: 'SUIT',
			fontWeight: 'regular',
		},
		dataLabels: {
			enabled: false,
		},
		tooltip: {
			style: {
				fontSize: '12px',
				fontFamily: 'SUIT',
			},
			fillSeriesColor: false,
			onDatasetHover: {
				highlightDataSeries: true,
			},
			y: {
				formatter: (val: number) => {
					return `${val.toLocaleString()}`;
				},
			},
		},
		plotOptions: {
			pie: {
				expandOnClick: false,
				donut: {
					labels: {
						show: max_label ?? false,
						name: { color: theme.colors.gray.gray60 },
						value: {
							offsetY: -10,
							fontSize: '18px',
							fontFamily: 'SUIT',
							fontWeight: 'bold',
							formatter(val) {
								return `${round(Number(val), 'round')}${unit ?? ''}`;
							},
						},
						total: {
							show: max_label ?? false,
							showAlways: max_label ?? false,
							fontSize: '12px',
							fontFamily: 'SUIT',
							fontWeight: 'bold',
							label: '',
							formatter: () => {
								return `${value?.[max_index] ?? ''}%`;
							},
						},
					},
				},
			},
		},
		labels: value_name ?? [],
		colors: colors,
	};

	return <ReactApexChart options={options} series={value} type="donut" />;
};

export default BasicDonutChart;
